import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTracker } from '../context/TrackerContext';
import { mentorLogin } from '../lib/api';

const EyeIcon: React.FC<{ show: boolean }> = ({ show }) => show ? (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
    <path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"/>
  </svg>
) : (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
    <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
  </svg>
);

const MentorLogin: React.FC = () => {
  const [passcode, setPasscode] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading]   = useState(false);
  const [mounted, setMounted]   = useState(false);
  const { loginAsMentor, loginAsCoordinator } = useTracker();
  const navigate = useNavigate();

  useEffect(() => { setTimeout(() => setMounted(true), 50); }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!passcode.trim()) return;
    setLoading(true);
    try {
      const res = await mentorLogin(passcode.trim());
      if (res.role === 'Student Coordinator') {
        loginAsCoordinator(res.mentorId, res.mentorName, res.coordToken || '');
        toast.success(`Welcome back, ${res.mentorName}!`);
        navigate('/tracker/admin/students');
      } else {
        loginAsMentor(res.mentorId, res.mentorName, res.role, res.seniorMentorId, !!res.isPlacementCoordinator, { isCareerStrategist: res.isCareerStrategist, csToken: res.csToken });
        toast.success(`Welcome back, ${res.mentorName}!`);
        // Clear startup key so popup always shows on fresh login
        sessionStorage.removeItem(`vt_startup_${res.mentorId}`);
        // A Career Strategist starts on the P2P students
        navigate(res.isCareerStrategist ? '/tracker/mentor/p2p' : '/tracker/mentor/quick-actions');
      }
    } catch (err: any) {
      toast.error(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden" style={{ background: 'linear-gradient(135deg,#1e1730 0%,#2b2240 50%,#3d2f5c 100%)' }}>

      {/* Background decoration */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-32 -left-32 w-96 h-96 rounded-full opacity-10" style={{ background: '#F0B429', filter: 'blur(80px)' }} />
        <div className="absolute -bottom-32 -right-32 w-96 h-96 rounded-full opacity-10" style={{ background: '#FF6535', filter: 'blur(80px)' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full opacity-5" style={{ background: '#F0B429', filter: 'blur(120px)' }} />
        {/* Dots grid */}
        <svg className="absolute inset-0 w-full h-full opacity-5" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="dots" x="0" y="0" width="30" height="30" patternUnits="userSpaceOnUse">
              <circle cx="1.5" cy="1.5" r="1.5" fill="white"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#dots)"/>
        </svg>
      </div>

      {/* Card */}
      <div className={`relative w-full max-w-sm mx-4 transition-all duration-700 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>

        {/* Glow */}
        <div className="absolute -inset-1 rounded-3xl opacity-30 blur-xl" style={{ background: 'linear-gradient(135deg,#F0B429,#FF6535)' }} />

        <div className="relative rounded-3xl overflow-hidden shadow-2xl" style={{ background: 'rgba(255,255,255,0.97)' }}>

          {/* Top accent bar */}
          <div className="h-1.5" style={{ background: 'linear-gradient(90deg,#F0B429,#FF6535,#F0B429)' }} />

          <div className="px-8 pt-8 pb-8">
            {/* Logo + title */}
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-4 shadow-lg"
                style={{ background: 'linear-gradient(135deg,#2b2240,#4e3a69)' }}>
                <span className="text-lg font-black" style={{ color: '#F0B429' }}>VT</span>
              </div>
              <h1 className="text-2xl font-black" style={{ color: '#2b2240' }}>Mentor Portal</h1>
              <p className="text-sm text-gray-400 mt-1 font-medium">Enter your passcode to continue</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Passcode field */}
              <div>
                <label className="block text-[10px] font-black uppercase tracking-widest mb-2" style={{ color: '#2b2240' }}>Passcode</label>
                <div className="relative">
                  <input
                    type={showPass ? 'text' : 'password'}
                    value={passcode}
                    onChange={e => setPasscode(e.target.value)}
                    placeholder="Enter your passcode"
                    required
                    disabled={loading}
                    autoFocus
                    className="w-full pl-4 pr-11 py-3.5 border-2 rounded-xl text-sm outline-none transition-all disabled:bg-gray-50 disabled:cursor-not-allowed font-medium"
                    style={{ borderColor: passcode ? '#2b2240' : '#e5e7eb', color: '#1e1730' }}
                  />
                  <button type="button" onClick={() => setShowPass(s => !s)} tabIndex={-1}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-lg transition-colors hover:bg-gray-100"
                    style={{ color: '#9ca3af' }}>
                    <EyeIcon show={showPass} />
                  </button>
                </div>
              </div>

              {/* Submit */}
              <button type="submit" disabled={loading || !passcode.trim()}
                className="w-full py-3.5 rounded-xl font-black text-sm transition-all duration-200 shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:scale-100"
                style={{ background: loading ? '#9ca3af' : 'linear-gradient(135deg,#2b2240,#4e3a69)', color: '#fff' }}>
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                    </svg>
                    Signing in…
                  </span>
                ) : 'Sign In'}
              </button>
            </form>

            {/* Footer */}
            <div className="mt-6 flex items-center justify-center gap-3">
              <div className="flex-1 h-px bg-gray-100" />
              <a href="/tracker/admin/login"
                className="text-xs font-semibold px-3 py-1.5 rounded-lg transition-all hover:bg-gray-50"
                style={{ color: '#9ca3af' }}>
                Admin portal →
              </a>
              <div className="flex-1 h-px bg-gray-100" />
            </div>
          </div>
        </div>

        {/* Footer brand */}
        <p className="text-center text-xs mt-4 font-medium" style={{ color: 'rgba(255,255,255,0.3)' }}>
          VistaTech Solutions · Student Tracker
        </p>
      </div>
    </div>
  );
};

export default MentorLogin;
